import { useAuth } from '@contexts/AuthContext';
import { type ApiErrorResponse, getUserFriendlyError, safeParseJson } from '@utils/api';
import { useCallback, useEffect, useState } from 'react';
import type { Leg } from '../../server/types/api';

// =============================================================================
// Types
// =============================================================================

/**
 * API response leg format (matches server Leg type but with string dates from JSON)
 */
interface ApiLeg extends Omit<Leg, 'gameDate' | 'gameStartsAt' | 'createdAt' | 'settledAt'> {
  gameDate?: string;
  gameStartsAt?: string | null;
  createdAt?: string;
  settledAt?: string;
}

import type { VoidReason } from '@parlay-picker/shared/settlement';

export interface TrackedLeg {
  id: number;
  date: string;
  player: string;
  // Prop display and components (consistent with server Leg type)
  prop: string; // Full display string: "Over 25.5 pts", "Lakers -5.5"
  statType: string; // Normalized stat type: pts, reb, spread, total, moneyline
  line: number | null; // Numeric line value (null for moneyline)
  overUnder: string | null; // "Over" or "Under" (null for spreads/moneyline)
  actual: number | null;
  hit: boolean | null | undefined;
  // Origin + flags (new schema)
  origin: 'ai' | 'manual' | 'bet';
  isTracked: boolean;
  isAIImproved: boolean;
  isBetVerified: boolean;
  parlayId?: number | null;
  team?: string | null;
  odds?: string | null;
  gameId?: string | null;
  opponentTeam?: string | null;
  playerId?: number | null;
  gameDate?: string;
  gameStartsAt?: string | null; // The tip; the deletion guard reads it before gameDate
  dnp?: boolean | null;
  voidReason?: VoidReason | null;
}

export interface UseLegsResult {
  legs: TrackedLeg[];
  setLegs: (newLegs: TrackedLeg[] | ((prev: TrackedLeg[]) => TrackedLeg[])) => void;
  loading: boolean;
  error: string | null;
}

// =============================================================================
// Helper Functions
// =============================================================================

// Convert DB leg format to frontend format
function dbToFrontend(dbLeg: ApiLeg): TrackedLeg {
  return {
    id: dbLeg.id,
    date: dbLeg.gameDate
      ? new Date(dbLeg.gameDate).toLocaleDateString()
      : new Date(dbLeg.createdAt || Date.now()).toLocaleDateString(),
    gameDate: dbLeg.gameDate, // Preserve raw ISO date for comparisons
    gameStartsAt: dbLeg.gameStartsAt,
    player: dbLeg.player,
    prop: dbLeg.prop,
    statType: dbLeg.statType,
    line: dbLeg.line ? parseFloat(dbLeg.line) : null,
    overUnder: dbLeg.overUnder ?? null,
    actual: dbLeg.actualValue ? parseFloat(dbLeg.actualValue) : null,
    hit: dbLeg.hit,
    // Origin + flags
    origin: dbLeg.origin || 'manual',
    isTracked: dbLeg.isTracked ?? false,
    isAIImproved: dbLeg.isAIImproved ?? false,
    isBetVerified: dbLeg.isBetVerified ?? false,
    parlayId: dbLeg.parlayId,
    team: dbLeg.team,
    odds: dbLeg.odds,
    gameId: dbLeg.gameId,
    opponentTeam: dbLeg.opponentTeam,
    dnp: dbLeg.dnp,
    voidReason: dbLeg.voidReason as VoidReason | null | undefined,
  };
}

// =============================================================================
// useLegs Hook
// =============================================================================

export function useLegs(): UseLegsResult {
  const { isAuthenticated, authFetch } = useAuth();
  const [legs, setLegsState] = useState<TrackedLeg[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load legs on mount or auth change
  useEffect(() => {
    async function loadLegs() {
      setLoading(true);
      setError(null);

      if (isAuthenticated) {
        try {
          const res = await authFetch('/api/legs');
          if (res.ok) {
            const data = await safeParseJson<{
              legs?: ApiLeg[];
              error?: string;
            }>(res);
            if ((data as ApiErrorResponse).error) throw new Error((data as ApiErrorResponse).error);
            const responseData = data as { legs?: ApiLeg[] };
            setLegsState((responseData.legs || []).map(dbToFrontend));
          } else {
            // On server error, preserve existing legs and set error state
            setError(`Failed to load legs (${res.status})`);
          }
        } catch (err) {
          // On network error, preserve existing legs and set error state
          setError(getUserFriendlyError(err as Error));
        }
      } else {
        // Not authenticated - no legs to show
        setLegsState([]);
      }

      setLoading(false);
    }

    loadLegs();
  }, [isAuthenticated, authFetch]);

  // Set all legs (for bulk operations)
  const setLegs = useCallback((newLegs: TrackedLeg[] | ((prev: TrackedLeg[]) => TrackedLeg[])) => {
    if (typeof newLegs === 'function') {
      setLegsState(newLegs);
    } else {
      setLegsState(newLegs);
    }
  }, []);

  return {
    legs,
    setLegs,
    loading,
    error,
  };
}
